import React, { useEffect, useMemo, useState } from "react";
import { fetchQuizQuestion, submitReviewAnswer, isAudioPlaybackAllowed, addWordBankEntry } from "../api";

/**
 * VocabularyStep — step 1 of the guided journey (learner view).
 *
 * New "learn by quizzing" flow:
 *   1. Every word / verb / expression starts as "to learn".
 *   2. The user selects any words they want to learn, then taps "Quiz selected".
 *   3. An inline mini-quiz (word_meaning multiple-choice) runs on that batch.
 *      Each correct answer marks the word as known on the backend
 *      (UserReviewState.is_known → topic.known_word_ids).
 *   4. The user repeats with the remaining words until every word is learnt.
 *   5. Once all words are learnt the host auto-completes the step.
 *
 * Data shapes (already present in TopicDetail's vocab block):
 *   - word.text            Arabic
 *   - word.transliteration
 *   - word.senses[].definition (primary sense) → meaning
 *   - word.word_type       "vocab" | "verb" | "expression"
 *   - knownIds / forgottenIds (Sets of word ids)
 *
 * Props
 *   sections           [{ label, items }]  already-filtered word arrays
 *   knownIds           Set<id>   words already learnt (is_known)
 *   forgottenIds       Set<id>   learnt then regressed — count as "to learn"
 *   reviewItemByWordId object|Map  wordId → reviewItemId (for quizzing)
 *   selectedWordId     id | null
 *   onWordSelect       (id, text, word_type) => void
 *   onPlayWord         (word) => void
 *   onBatchComplete    () => void   fired after a quiz batch so the host can
 *                                   refresh known_word_ids + check completion
 *   completed          bool
 *   stepLabel          string
 */
export default function VocabularyStep({
  sections = [],
  knownIds = new Set(),
  forgottenIds = new Set(),
  reviewItemByWordId = {},
  verbFormsByWordId = {},
  particlesByWordId = {},
  selectedWordId = null,
  onWordSelect = () => {},
  onPlayWord = () => {},
  onBatchComplete = () => {},
  onAllLearnt = () => {},
  completed = false,
  stepLabel = "step 1 of 5",
  canRemove = false,
  onRemoveWord = () => {},
  onReclassify = () => {},
}) {
  const meaningOf = (w) =>
    w.senses?.find((s) => s.is_primary)?.definition ||
    w.conjugation_info?.definition ||
    w.senses?.[0]?.definition ||
    w.meaning ||
    "";

  const reviewIdOf = (id) =>
    reviewItemByWordId instanceof Map
      ? reviewItemByWordId.get(id)
      : reviewItemByWordId?.[id];

  const isLearnt = (w) => knownIds.has(w.id);

  // ── Word Bank add state ───────────────────────────────────────────────────
  // wordId → "adding" | "added" | "exists" | "error"
  const [wbById, setWbById] = useState(() => ({}));
  const addToWordBank = async (w) => {
    const cur = wbById[w.id];
    if (cur === "adding" || cur === "added" || cur === "exists") return;
    setWbById((m) => ({ ...m, [w.id]: "adding" }));
    try {
      const res = await addWordBankEntry({ word_id: w.id });
      setWbById((m) => ({ ...m, [w.id]: res?.already_in_bank ? "exists" : "added" }));
    } catch {
      setWbById((m) => ({ ...m, [w.id]: "error" }));
    }
  };

  // ── Selection state ───────────────────────────────────────────────────────
  const [selected, setSelected] = useState(() => new Set());
  // The batch currently being quizzed (null when browsing the list).
  const [batch, setBatch] = useState(null);

  const allItems = useMemo(
    () => sections.flatMap((s) => s.items || []),
    [sections]
  );

  // Multi-word phrases (most expressions) have no backing ReviewItem on the
  // backend, so they can't be quizzed. They're shown as reference-only and
  // don't gate completion.
  const isQuizzable = (w) => !!reviewIdOf(w.id);
  // A word can be picked for a quiz only if it's quizzable and not yet learnt.
  const isSelectable = (w) => !isLearnt(w) && isQuizzable(w);

  // Progress + the completion gate are based on quizzable words only.
  const quizzable = allItems.filter(isQuizzable);
  const toLearn = quizzable.filter((w) => !isLearnt(w));
  const learntCount = quizzable.length - toLearn.length;
  // True when there's genuinely nothing to quiz (e.g. a topic of only phrases).
  const nothingToQuiz = allItems.length > 0 && quizzable.length === 0;

  // Drop any selections that became learnt / vanished between renders.
  useEffect(() => {
    setSelected((prev) => {
      const next = new Set();
      for (const id of prev) {
        const w = allItems.find((x) => x.id === id);
        if (w && isSelectable(w)) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knownIds, allItems]);

  // Auto-complete the step the moment every quizzable word is learnt. Guarded
  // by `completed` so it fires once; the host flips that flag via onAllLearnt.
  // (A topic with nothing to quiz can't auto-complete — see the manual
  // continue button below.)
  useEffect(() => {
    if (!completed && quizzable.length > 0 && toLearn.length === 0) {
      onAllLearnt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completed, quizzable.length, toLearn.length]);

  const toggle = (w) => {
    if (!isSelectable(w)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(w.id)) next.delete(w.id);
      else next.add(w.id);
      return next;
    });
  };

  const selectableInSection = (items) => items.filter(isSelectable);

  // Pool of every (real) plural form in the topic — used as distractors for the
  // "select the plural" questions (with a singular-word fallback below).
  const pluralPool = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const w of allItems) {
      for (const p of w.plurals || []) {
        if (isRealPluralForm(p.form) && !seen.has(p.form)) {
          seen.add(p.form);
          out.push({ form: p.form, transliteration: p.transliteration || "" });
        }
      }
    }
    return out;
  }, [allItems]);

  const wordPool = useMemo(
    () => allItems.map((w) => ({ form: w.text, transliteration: w.transliteration || "" })),
    [allItems]
  );

  const startQuiz = (wordIds) => {
    const list = wordIds
      .map((id) => {
        const w = allItems.find((x) => x.id === id);
        const reviewItemId = reviewIdOf(id);
        if (!w || !reviewItemId) return null;
        return {
          wordId: id,
          reviewItemId,
          text: w.text,
          transliteration: w.transliteration || "",
          meaning: meaningOf(w),
          // Real plural forms only (skip "—"/dash placeholders that mean "no
          // plural"); drives the plural pass.
          plurals: (w.plurals || []).filter((p) => isRealPluralForm(p.form)),
          // Verb-form drill questions (eligible verbs only); drives the forms pass.
          verbForms: verbFormsByWordId[id] || [],
          // "Which particle?" questions (verbs with governing particles).
          particles: particlesByWordId[id] || [],
        };
      })
      .filter(Boolean);
    if (list.length === 0) return;
    setBatch(list);
  };

  const finishQuiz = () => {
    setBatch(null);
    setSelected(new Set());
    onBatchComplete();
  };

  // ── Active mini-quiz ──────────────────────────────────────────────────────
  if (batch) {
    return (
      <VocabLearnQuiz
        batch={batch}
        pluralPool={pluralPool}
        wordPool={wordPool}
        onClose={finishQuiz}
      />
    );
  }

  const visible = sections.filter((s) => s.items && s.items.length > 0);
  const totalSelectable = allItems.filter(isSelectable).length;

  // Render helper (NOT a nested component): defining a component inside the body
  // gives it a new type each render, which would unmount/remount every row on
  // any state change — losing focus and the list's scroll position when a word
  // is clicked. Calling it as a function keeps the rows reconciled by key.
  const renderRow = (w) => {
    const isSel = w.id === selectedWordId;
    const learnt = isLearnt(w);
    const checked = selected.has(w.id);
    const meaning = meaningOf(w);
    const quizzableW = isQuizzable(w);
    const selectable = isSelectable(w);
    // reference-only = a phrase with no quiz item; shown but doesn't gate the step.
    const referenceOnly = !quizzableW && !learnt;
    return (
      <li key={w.id}>
        <div className="flex items-center gap-1.5">
          {/* Selection checkbox / learnt tick / reference marker */}
          {referenceOnly ? (
            <span
              title="Reference only — practised in the dialogue, not quizzed here"
              aria-label="Reference only"
              className="shrink-0 w-7 h-7 inline-flex items-center justify-center text-ink-300"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-ink-300" />
            </span>
          ) : (
            <button
              type="button"
              onClick={() => toggle(w)}
              disabled={!selectable}
              aria-pressed={checked}
              aria-label={learnt ? "Learnt" : checked ? "Selected" : "Select to learn"}
              title={
                learnt ? "Learnt" : checked ? "Selected" : "Select to learn"
              }
              className={`shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-md border transition-colors ${
                learnt
                  ? "bg-ink-500 border-ink-500 text-white cursor-default"
                  : checked
                  ? "bg-gold-500 border-gold-500 text-white"
                  : "bg-parchment-50 border-parchment-300 text-transparent hover:border-gold-400"
              }`}
            >
              <CheckIcon className="w-4 h-4" />
            </button>
          )}

          <button
            type="button"
            onClick={() => onWordSelect(w.id, w.text, w.word_type)}
            className={`surface-glass surface--interactive flex-1 min-w-0 flex items-center gap-3 px-3 py-2.5 text-left ${isSel ? "surface--active" : ""}`}
          >
            <span dir="rtl" className="font-arabic text-xl text-ink-800 min-w-[78px]">
              {w.text}
            </span>
            <span className="flex flex-col flex-1 min-w-0">
              {w.transliteration && (
                <span className="text-xs text-ink-400 italic truncate">
                  {w.transliteration}
                </span>
              )}
              {meaning && (
                <span className="text-sm text-ink-700 truncate">{meaning}</span>
              )}
            </span>
            <span
              className={`text-[11px] px-2.5 py-0.5 rounded-full whitespace-nowrap ${
                learnt
                  ? "bg-ink-50 text-ink-700"
                  : referenceOnly
                  ? "bg-parchment-200 text-ink-400"
                  : "bg-dt-gold-bg text-dt-gold-bright"
              }`}
            >
              {learnt
                ? "learnt"
                : referenceOnly
                ? "reference"
                : forgottenIds.has(w.id)
                ? "review"
                : "to learn"}
            </span>
          </button>
          {/* Add to Word Bank */}
          {(() => {
            const st = wbById[w.id];
            const done = st === "added" || st === "exists";
            return (
              <button
                type="button"
                onClick={() => addToWordBank(w)}
                disabled={st === "adding"}
                title={
                  st === "added" ? "Added to your Word Bank"
                  : st === "exists" ? "Already in your Word Bank"
                  : st === "error" ? "Could not add — tap to retry"
                  : "Add to Word Bank"
                }
                aria-label="Add to Word Bank"
                className={`shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-lg border transition-colors disabled:opacity-50 ${
                  done
                    ? "border-ink-500 bg-ink-50 text-ink-600"
                    : st === "error"
                    ? "border-red-200 bg-parchment-50 text-red-500 hover:bg-red-50"
                    : "border-parchment-300 bg-parchment-50 text-ink-500 hover:bg-parchment-100 hover:text-ink-700"
                }`}
              >
                {st === "adding" ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                ) : done ? (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 2h12a1 1 0 011 1v18l-7-4-7 4V3a1 1 0 011-1z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 3h12a1 1 0 011 1v17l-7-4-7 4V4a1 1 0 011-1z" />
                  </svg>
                )}
              </button>
            );
          })()}
          {/* Hidden when TTS is blocked (e.g. seatless institute admin). */}
          {isAudioPlaybackAllowed() && (
            <button
              type="button"
              onClick={() => onPlayWord(w)}
              title="Play audio"
              aria-label="Play audio"
              className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-lg border border-parchment-300 bg-parchment-50 text-ink-500 hover:bg-parchment-100 hover:text-ink-700 transition-colors"
            >
              <PlayIcon />
            </button>
          )}
          {/* Per-word type (N/V/E) reclassify buttons removed — the word type is
              set from the Word Detail panel's "Type in topic" toggle instead. */}
          {canRemove && (
            <button
              type="button"
              onClick={() => onRemoveWord(w)}
              title="Remove from this list"
              aria-label="Remove from list"
              className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-lg border border-red-200 bg-parchment-50 text-red-500 hover:bg-red-50 transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className="animate-rise">
      {/* Step heading is provided by the Guided Journey section head above. */}
      <p className="text-sm text-ink-400 mb-4">
        Select the words you want to learn, then quiz yourself. Answer a word
        correctly and it's marked as learnt. Learn every word to finish this step.
      </p>

      {/* Learnt progress */}
      {quizzable.length > 0 && (
        <div className="surface-glass mb-4 rounded-xl px-3.5 py-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-ink-600">
              {learntCount} of {quizzable.length} learnt
            </span>
            {toLearn.length > 0 && (
              <span className="text-[11px] text-ink-400">
                {toLearn.length} to go
              </span>
            )}
          </div>
          <div className="progress-track">
            <div
              className="progress-fill"
              style={{
                width: `${Math.round((learntCount / quizzable.length) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Sticky selection bar — appears once words are selected so the user can
          launch the quiz without hunting for the button at the bottom. Pinned
          to the top of the scroll area so it's never clipped by the tab bar. */}
      {selected.size > 0 && (
        <div className="sticky -top-4 md:-top-6 z-20 -mx-3 sm:-mx-4 mb-4 px-3 sm:px-4 py-2.5 backdrop-blur border-y shadow-sm flex items-center justify-between gap-3" style={{ background: "rgba(14,33,26,0.95)", borderColor: "var(--panel-line)" }}>
          <span className="text-sm font-medium shrink-0" style={{ color: "var(--parchment)" }}>
            {selected.size} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-[11px] font-medium px-1 hover:brightness-110"
              style={{ color: "var(--parchment-dim)" }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => startQuiz(Array.from(selected))}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 min-h-[40px] text-sm font-semibold rounded-lg shadow-sm hover:brightness-105 focus:outline-none transition-[filter]"
              style={{ background: "var(--gold)", color: "var(--ink)" }}
            >
              Quiz {selected.size} word{selected.size === 1 ? "" : "s"} →
            </button>
          </div>
        </div>
      )}

      <div className="space-y-5">
        {visible.map(({ label, items }) => {
          const selectable = selectableInSection(items);
          const allChosen =
            selectable.length > 0 && selectable.every((w) => selected.has(w.id));
          return (
            <div key={label}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wide text-ink-400">
                  {label} · {items.length}
                </div>
                {selectable.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (allChosen) selectable.forEach((w) => next.delete(w.id));
                        else selectable.forEach((w) => next.add(w.id));
                        return next;
                      })
                    }
                    className="text-[11px] font-medium text-gold-600 hover:text-gold-700"
                  >
                    {allChosen ? "Clear" : "Select all"}
                  </button>
                )}
              </div>
              <ul className="list-none p-0 m-0 flex flex-col gap-1.5">
                {items.map((w) => renderRow(w))}
              </ul>
            </div>
          );
        })}
        {visible.length === 0 && (
          <p className="text-sm text-ink-400 py-6 text-center">
            No vocabulary in this topic yet.
          </p>
        )}
      </div>

      {/* Quiz launcher */}
      {totalSelectable > 0 && (
        <div className="mt-5">
          <button
            type="button"
            onClick={() => startQuiz(Array.from(selected))}
            disabled={selected.size === 0}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 min-h-[48px] text-sm font-semibold rounded-xl text-dt-ink shadow-sm bg-dt-gold hover:brightness-105 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {selected.size === 0
              ? "Select words to quiz"
              : `Quiz ${selected.size} selected word${selected.size === 1 ? "" : "s"}`}
          </button>
        </div>
      )}

      {/* Edge case: nothing in this topic can be quizzed (all phrases). Offer a
          manual continue so the step isn't a dead-end. */}
      {nothingToQuiz && !completed && (
        <div className="mt-5">
          <button
            type="button"
            onClick={() => onAllLearnt()}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 min-h-[48px] text-sm font-semibold rounded-xl text-dt-ink shadow-sm bg-dt-gold hover:brightness-105 focus:outline-none transition-colors"
          >
            Continue to Reading →
          </button>
        </div>
      )}
    </div>
  );
}

// A plural is "real" only if it has content beyond a placeholder dash
// ("—" / "–" / "-"), which some entries use to mean "no plural".
function isRealPluralForm(form) {
  const t = (form || "").trim();
  if (!t) return false;
  return !/^[—–-]+$/.test(t);
}

function shuffleArr(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Build a "select the plural" multiple-choice question for a noun.
// Correct = the word's first plural form; distractors come from other topic
// plurals, falling back to other singulars so there are always enough options.
function buildPluralQuestion(word, pluralPool, wordPool) {
  const ownForms = new Set((word.plurals || []).map((p) => p.form));
  const correct = word.plurals[0].form;
  let pool = pluralPool.filter((p) => !ownForms.has(p.form));
  if (pool.length < 3) {
    const extra = wordPool.filter((w) => w.form !== word.text && !ownForms.has(w.form));
    pool = pool.concat(extra);
  }
  const seen = new Set([correct]);
  const distractors = [];
  for (const p of shuffleArr(pool)) {
    if (!seen.has(p.form)) {
      seen.add(p.form);
      distractors.push(p.form);
    }
    if (distractors.length >= 3) break;
  }
  return { correct, choices: shuffleArr([correct, ...distractors]) };
}

/**
 * VocabLearnQuiz — inline two-pass quiz over a batch of words.
 *
 * Pass 1 (recognition): Arabic → English meaning, via the backend word_meaning
 *   generator + SRS submit (a correct answer flips the word to "known" =
 *   learnt). When a noun has a plural, the correct feedback also teaches it.
 * Pass 2 (plural — nouns with a plural only): pick the correct Arabic plural
 *   from the singular + English. Built client-side; reinforcement only, it does
 *   not gate "learnt".
 */
function VocabLearnQuiz({ batch, pluralPool = [], wordPool = [], onClose }) {
  // Step sequence: every word's meaning first, then form questions (verb forms
  // for eligible verbs, else the plural for nouns) — spaced from the meanings.
  const steps = useMemo(() => {
    const s = batch.map((w) => ({ kind: "meaning", word: w }));
    for (const w of batch) {
      if (w.verbForms && w.verbForms.length > 0) {
        w.verbForms.forEach((q) => s.push({ kind: "verbform", word: w, question: q }));
      } else if (w.plurals && w.plurals.length > 0) {
        s.push({ kind: "plural", word: w });
      }
    }
    // Particle questions (verbs governed by a preposition) — independent of the
    // verb-form/plural pass, so a verb can be drilled on both.
    for (const w of batch) {
      (w.particles || []).forEach((q) => s.push({ kind: "particle", word: w, question: q }));
    }
    return s;
  }, [batch]);

  // Precompute plural questions once so choices stay stable across re-renders.
  const pluralQuestions = useMemo(() => {
    const map = {};
    steps.forEach((st, i) => {
      if (st.kind === "plural") map[i] = buildPluralQuestion(st.word, pluralPool, wordPool);
    });
    return map;
  }, [steps, pluralPool, wordPool]);

  const meaningTotal = batch.length;

  const [index, setIndex] = useState(0);
  const [question, setQuestion] = useState(null); // fetched word_meaning question
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [choice, setChoice] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [learntCount, setLearntCount] = useState(0);

  const step = steps[index];
  const isMeaning = step?.kind === "meaning";
  const isPlural = step?.kind === "plural";
  const isVerbForm = step?.kind === "verbform";
  const isParticle = step?.kind === "particle";
  const word = step?.word;
  const isLast = index >= steps.length - 1;

  // Load (meaning) or prepare (plural) the current step.
  useEffect(() => {
    setChoice(null);
    setSubmitted(false);
    if (!step) return;
    if (step.kind !== "meaning") {
      setLoading(false);
      setError(null);
      setQuestion(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setQuestion(null);
    fetchQuizQuestion(step.word.reviewItemId, "word_meaning")
      .then((data) => {
        if (!cancelled) setQuestion(data.question);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load this question.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, steps]);

  const pq = isPlural ? pluralQuestions[index] : null;
  const vfq = isVerbForm ? step.question : null;
  const pcq = isParticle ? step.question : null;

  let choices = [];
  let correctChoice = null;
  let ready = false;
  if (isMeaning) {
    choices = question?.choices || [];
    correctChoice = question?.correct_answer?.choice;
    ready = !!question;
  } else if (isPlural) {
    choices = pq?.choices || [];
    correctChoice = pq?.correct;
    ready = !!pq;
  } else if (isVerbForm) {
    choices = (vfq?.choices || []).map((c) => c.text);
    correctChoice = (vfq?.choices || []).find((c) => c.form === vfq.correct_form)?.text;
    ready = !!vfq;
  } else if (isParticle) {
    choices = pcq?.choices || [];
    correctChoice = pcq?.correct_particle;
    ready = !!pcq;
  }

  const submit = async () => {
    if (choice == null || submitted) return;
    const isCorrect = choice === correctChoice;
    setSubmitted(true);
    if (isMeaning) {
      if (isCorrect) setLearntCount((c) => c + 1);
      try {
        await submitReviewAnswer({
          review_item_id: word.reviewItemId,
          question_type: question?.question_type || "word_meaning",
          is_correct: isCorrect,
          user_answer: { choice },
          correct_answer: { choice: correctChoice },
          // Learning here permanently marks the word learnt for the Vocabulary
          // step — a sticky flag nothing else (quiz, Word Bank) can clear.
          mark_vocab_learned: true,
        });
      } catch {
        // Network failure shouldn't trap the learner; next refresh reconciles.
      }
    }
    // Plural questions are reinforcement only — no SRS submit.
  };

  const next = () => {
    if (isLast) onClose();
    else setIndex((i) => i + 1);
  };

  const pluralsText = (w) =>
    (w?.plurals || [])
      .map((p) => (p.transliteration ? `${p.form} (${p.transliteration})` : p.form))
      .join("، ");

  return (
    <div className="animate-rise">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gold-50 border-[1.5px] border-gold-400 text-gold-500 text-sm font-bold">
            1
          </span>
          <h3 className="text-lg font-display font-semibold text-ink-800">
            Learn the words
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs font-medium text-ink-400 hover:text-ink-600"
        >
          End quiz
        </button>
      </div>

      {/* Batch progress */}
      <div className="flex items-center gap-2 mb-4">
        <div className="progress-track flex-1">
          <div
            className="progress-fill"
            style={{ width: `${((index + (submitted ? 1 : 0)) / steps.length) * 100}%` }}
          />
        </div>
        <span className="text-[11px] text-ink-400 whitespace-nowrap">
          {index + 1} / {steps.length}
        </span>
      </div>

      {loading && (
        <p className="text-sm text-ink-400 py-10 text-center">Loading question…</p>
      )}

      {error && !loading && (
        <div className="py-8 text-center">
          <p className="text-sm text-rose-600 mb-3">{error}</p>
          <button
            type="button"
            onClick={next}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-ink-600 text-white hover:bg-ink-700"
          >
            Skip
          </button>
        </div>
      )}

      {ready && !loading && !error && (
        <div>
          {/* Prompt */}
          <div className="surface-glass px-4 py-5 text-center mb-4">
            {isVerbForm ? (
              <>
                <p className="text-[11px] uppercase tracking-wide text-ink-400 mb-0.5">
                  {vfq.target_label_en}
                </p>
                <p dir="rtl" className="font-arabic text-base text-ink-600 mb-3">
                  {vfq.target_label_ar}
                </p>
                <p className="text-[10px] uppercase tracking-wide text-ink-300 mb-1">
                  Past tense
                </p>
                <p dir="rtl" className="font-arabic text-3xl text-ink-800">
                  {vfq.past}
                </p>
                {(vfq.past_transliteration || word.transliteration) && (
                  <p className="text-xs text-ink-400 italic mt-1.5">
                    {vfq.past_transliteration || word.transliteration}
                  </p>
                )}
                {word.meaning && <p className="text-xs text-ink-400 mt-1">{word.meaning}</p>}
              </>
            ) : isParticle ? (
              <>
                <p className="text-[11px] uppercase tracking-wide text-ink-400 mb-2">
                  Which particle (حرف) completes the meaning?
                </p>
                <p dir="rtl" className="font-arabic text-3xl text-ink-800">
                  {pcq.verb_text} …
                </p>
                {pcq.meaning && (
                  <p className="text-xs text-ink-500 mt-1.5">to mean: “{pcq.meaning}”</p>
                )}
              </>
            ) : (
              <>
                <p className="text-[11px] uppercase tracking-wide text-ink-400 mb-2">
                  {isMeaning ? "What does this word mean?" : "Which is the plural?"}
                </p>
                <p dir="rtl" className="font-arabic text-3xl text-ink-800">
                  {isMeaning ? question.prompt?.arabic || word.text : word.text}
                </p>
                {isMeaning ? (
                  (question.prompt?.transliteration || word.transliteration) && (
                    <p className="text-xs text-ink-400 italic mt-1.5">
                      {question.prompt?.transliteration || word.transliteration}
                    </p>
                  )
                ) : (
                  <p className="text-xs text-ink-400 mt-1.5">
                    {word.transliteration && <span className="italic">{word.transliteration}</span>}
                    {word.meaning && <span> · {word.meaning}</span>}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Choices */}
          <ul className="list-none p-0 m-0 flex flex-col gap-2">
            {choices.map((c, i) => {
              const isPicked = choice === c;
              const isAnswer = c === correctChoice;
              let cls = "surface-glass surface--interactive text-dt-parchment";
              if (submitted) {
                if (isAnswer)
                  cls = "border-dt-teal bg-dt-teal-bg text-dt-teal";
                else if (isPicked)
                  cls = "border-dt-rose bg-dt-rose-bg text-dt-rose";
                else cls = "surface-glass text-dt-parchment-dim";
              } else if (isPicked) {
                cls = "surface-glass surface--active text-dt-gold-bright";
              }
              return (
                <li key={`${c}-${i}`}>
                  <button
                    type="button"
                    disabled={submitted}
                    onClick={() => setChoice(c)}
                    className={`w-full px-4 py-3 rounded-xl border text-sm transition-colors flex items-center justify-between gap-2 ${cls} disabled:cursor-default ${
                      isMeaning ? "text-left" : ""
                    }`}
                  >
                    <span dir={isMeaning ? undefined : "rtl"} className={isMeaning ? "" : "font-arabic text-lg"}>
                      {c}
                    </span>
                    {submitted && isAnswer && <CheckIcon className="w-4 h-4 text-ink-600 shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Feedback + actions */}
          <div className="mt-5">
            {submitted && (
              <div className="mb-3">
                <p
                  className={`text-sm font-medium ${
                    choice === correctChoice ? "text-ink-700" : "text-rose-600"
                  }`}
                >
                  {isMeaning
                    ? choice === correctChoice
                      ? "✓ Correct — marked as learnt."
                      : "Not quite — give this one another try later."
                    : choice === correctChoice
                    ? "✓ Correct!"
                    : `Not quite — the ${isPlural ? "plural" : isParticle ? "particle" : "answer"} is ${correctChoice}.`}
                </p>
                {/* Teach the plural alongside a correct meaning answer. */}
                {isMeaning && choice === correctChoice && word.plurals?.length > 0 && (
                  <p className="text-xs text-ink-500 mt-1">
                    <span className="text-ink-400">Plural: </span>
                    <span dir="rtl" className="font-arabic">{pluralsText(word)}</span>
                  </p>
                )}
              </div>
            )}
            {!submitted ? (
              <button
                type="button"
                onClick={submit}
                disabled={choice == null}
                className="w-full inline-flex items-center justify-center px-4 py-3 min-h-[48px] text-sm font-semibold rounded-xl text-dt-ink shadow-sm bg-dt-gold hover:brightness-105 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Check answer
              </button>
            ) : (
              <button
                type="button"
                onClick={next}
                className="w-full inline-flex items-center justify-center px-4 py-3 min-h-[48px] text-sm font-semibold rounded-xl text-dt-ink shadow-sm bg-dt-gold hover:brightness-105 focus:outline-none transition-colors"
              >
                {isLast ? `Done · ${learntCount}/${meaningTotal} learnt` : "Next →"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CheckIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.4} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
function PlayIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}
